import { apiUnloggedInstance } from '@/api/api.js'
import Vue from 'vue'
import {
  SURVEY_ANSWER_TYPES,
  SURVEY_QUESTION_TYPES,
} from '../../helpers/constants'
import { checkIfAnswered } from '../../helpers/screenerCheckIfAnswered'
const api = apiUnloggedInstance()

export default {
  /**
   *
   * @param {*} param0
   * @param {{ key: string, forceNew: boolean, email?: string, screenerVersion?: number }} payload - Survey key (ID) and forceNew if needs to clear cookies
   */
  async startPublicSurveyForm({ commit, dispatch }, payload) {
    try {
      commit('setLoading', true)
      const { key, forceNew, email, screenerVersion = 1 } = payload
      let url = `/public/surveys/${key}`
      const data = {
        forceNew,
      }

      if (screenerVersion === 2) {
        url = `/public-presentation/surveys/${key}`
        data.email = email

        if (!email) {
          commit(
            'appStates/setSnackbarNotification',
            'Field e-mail is required.',
            { root: true },
          )
          commit('setLoading', false)
          return { error: true }
        }
      }

      const response = await api.post(url, data)

      commit('setSurvey', response.data)
      commit('setLoading', false)
      commit('setSurveyAdditionalScores', [])
      commit('setSurveyResults', null)

      return response.data
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
      if (error?.response?.status === 400) return { error: true }
      return null
    }
  },
  /**
   *
   * @param {*} param0
   * @param {{ answerId: number, content?: string | null, selected?: boolean, value?: number, screenerVersion?: number }} params
   */
  async answerPublicSurveyForm({ commit, dispatch }, params) {
    try {
      const {
        sectionId,
        questionId,
        answerId,
        content,
        selected,
        value,
        parentAnswerId,
        parentQuestionId,
        rootParentAnswerId,
        rootParentQuestionId,
        screenerVersion = 1,
      } = params
      const data = {
        content: content || null,
        selected: selected || false,
        value: value || null,
      }

      let url = `/public/surveys/answers/${answerId}`
      if (parentAnswerId) {
        url = `/public/surveys/answers/${parentAnswerId}?embeddedAnswerId=${answerId}`
      }
      if (rootParentAnswerId) {
        url = `/public/surveys/answers/${rootParentAnswerId}?embeddedAnswerId=${parentAnswerId}&secondLevelEmbeddedAnswerId=${answerId}`
      }

      if (screenerVersion === 2) {
        url = `/public-presentation/surveys/answers/${answerId}`
        if (parentAnswerId) {
          url = `/public-presentation/surveys/answers/${parentAnswerId}?embeddedAnswerId=${answerId}`
        }
        if (rootParentAnswerId) {
          url = `/public-presentation/surveys/answers/${rootParentAnswerId}?embeddedAnswerId=${parentAnswerId}&secondLevelEmbeddedAnswerId=${answerId}`
        }
      }

      const response = await api.put(url, data)

      return response
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      return error
    }
  },
  /**
   *
   * @param {*} param0
   * @param {{ sectionId: string, allowEmpty?: boolean, screenerVersion?: number }} params
   * @returns {boolean} - success status
   */
  async completePublicSurveySection({ state, commit, dispatch }, params) {
    try {
      const { sectionId, allowEmpty, screenerVersion = 1 } = params

      // Check if all questions has answers
      const section = state.survey?.sections?.find?.(el => el.id === sectionId)
      const questions = section?.questions

      // Set array with results (has answer or not)
      const filledQuestions = questions.map(checkIfAnswered)

      // If there is no at least one question not answered and do not allow empty, then return false
      if (filledQuestions.includes(false) && !allowEmpty) {
        return false
      }

      let url = `/public/surveys/sections/${sectionId}/complete`

      if (screenerVersion === 2) {
        url = `/public-presentation/surveys/sections/${sectionId}/complete`
      }

      // Update completed status
      await api.post(url)
      commit('setSectionComplete', { sectionId, completed: true })
      return true
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true }) // @TODO: Maybe we should check error message, and change that to "Sorry, there is an error while saving this section. Try again." - or something like that.
      return false
    }
  },
  /**
   *
   * @param {*} param0
   * @param {{ screenerVersion?: number }} params
   * @returns {boolean} - success status
   */
  async completePublicSurvey({ commit, dispatch }, params) {
    try {
      commit('setLoading', true)
      const screenerVersion = params?.screenerVersion || 1
      let url = '/public/surveys/complete'

      if (screenerVersion === 2) {
        url = '/public-presentation/surveys/complete'
      }

      const response = await api.put(url)
      let riskPoints = response?.data?.rfsPoints ?? null
      let protectivePoints = response?.data?.bhvsPoints ?? null
      let additionalScores = []
      if (riskPoints !== null) {
        additionalScores = [
          ...additionalScores,
          { title: 'Risk Factor', value: riskPoints },
        ]
      }
      if (protectivePoints !== null) {
        additionalScores = [
          ...additionalScores,
          { title: 'Protective Factor', value: protectivePoints },
        ]
      }
      commit('setSurveyAdditionalScores', additionalScores)
      commit('setSurveyResults', response?.data?.result || null)
      commit('setLoading', false)
      return true
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
      return false
    }
  },
  /**
   *
   * @param {*} param0
   * @param {{ firstName: string, lastName: string, email: string, screenerVersion?: number }} params
   */
  async submitPublicSurvey({ commit, dispatch }, params) {
    try {
      commit('setLoading', true)
      const {
        firstName,
        lastName,
        email,
        screenerVersion = 1,
        skipSubmitForm = false,
      } = params
      if (screenerVersion === 2) {
        // no submit, so skip this req
        commit('setLoading', false)
        return true
      }
      // check data if not skiping submit form body
      if (!skipSubmitForm && (!firstName || !lastName || !email)) {
        commit(
          'appStates/setSnackbarNotification',
          'All fields are required. Please fill all fields.',
          { root: true },
        )
        commit('setLoading', false)
        return false
      }

      const response = await api.post(
        '/public/surveys/submit',
        skipSubmitForm
          ? null
          : {
              firstname: firstName,
              lastname: lastName,
              email,
            },
      )
      if (response?.data?.result) {
        let riskPoints = response?.data?.rfsPoints ?? null
        let protectivePoints = response?.data?.bhvsPoints ?? null
        let additionalScores = []
        if (riskPoints !== null) {
          additionalScores = [
            ...additionalScores,
            { title: 'Risk Factor', value: riskPoints },
          ]
        }
        if (protectivePoints !== null) {
          additionalScores = [
            ...additionalScores,
            { title: 'Protective Factor', value: protectivePoints },
          ]
        }
        commit('setSurveyAdditionalScores', additionalScores)
        commit('setSurveyResults', response?.data?.result || null)
      }

      return true
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
      return false
    }
  },
  updateConfirmationAnswer({ commit }, params) {
    const { answerId, content } = params
    commit('setConfirmationAnswer', { answerId, content })
  },
  /**
   *
   * @param {*} param0
   * @param {{ answerId: number, content?: string | null, selected?: boolean, value?: number, screenerVersion?: number }} params
   */
  async answerFeedbackForPublicSurveyForm({ commit, dispatch }, params) {
    try {
      const {
        questionId,
        answerId,
        content,
        selected,
        value,
        parentAnswerId,
        parentQuestionId,
        rootParentAnswerId,
        rootParentQuestionId,
        screenerVersion = 1,
      } = params
      const data = {
        content: content || null,
        selected: selected || false,
        value: value || null,
      }

      let url = `/public/surveys/feedback/${answerId}`
      if (parentAnswerId) {
        url = `/public/surveys/feedback/${parentAnswerId}?embeddedAnswerId=${answerId}`
      }
      if (rootParentAnswerId) {
        url = `/public/surveys/feedback/${rootParentAnswerId}?embeddedAnswerId=${parentAnswerId}&secondLevelEmbeddedAnswerId=${answerId}`
      }

      if (screenerVersion === 2) {
        url = `/public-presentation/surveys/feedback/${answerId}`
        if (parentAnswerId) {
          url = `/public-presentation/surveys/feedback/${parentAnswerId}?embeddedAnswerId=${answerId}`
        }
        if (rootParentAnswerId) {
          url = `/public-presentation/surveys/feedback/${rootParentAnswerId}?embeddedAnswerId=${parentAnswerId}&secondLevelEmbeddedAnswerId=${answerId}`
        }
      }

      const response = await api.post(url, data)

      return response
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      return error
    }
  },
}
